/**
 * PageContainer — 페이지 최상단 flex 컬럼 쉘 (단순형).
 *
 * 【역할】
 * - `fw-page` 클래스 + flex column + flex:1 + minHeight:0 의 기본 컨테이너.
 * - 툴바/차트/패널을 세로로 쌓는 대시보드·상세 화면에서 사용.
 *
 * 【주요 기능】
 * - antd theme token (marginSM) 을 기본 gap 으로 사용.
 * - gap / style 로 커스터마이즈.
 *
 * 【Props】
 * - children: ReactNode — 페이지 본문.
 * - gap?: number — 자식 간 gap (기본 theme.marginSM).
 * - style?: CSSProperties — 외곽 스타일 오버라이드.
 *
 * 【사용 예시】
 * ```tsx
 * <PageContainer gap={16}>
 *   <Card />
 *   <DataTable ... />
 * </PageContainer>
 * ```
 */
import { type ReactNode, type CSSProperties } from 'react'
import { theme as antdTheme } from 'antd'

export interface PageContainerProps {
  /** 페이지 본문. */
  children: ReactNode
  /** 자식 간 gap (기본 theme.marginSM). */
  gap?: number
  /** 외곽 스타일 오버라이드. */
  style?: CSSProperties
}

/**
 * PageContainer — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function PageContainer({ children, gap, style }: PageContainerProps) {
  const { token } = antdTheme.useToken()
  return (
    <div
      className="fw-page"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        gap: gap ?? token.marginSM,
        ...style
      }}
    >
      {children}
    </div>
  )
}
